import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { requestInProcess } from 'selectors';
import { useSelector } from 'react-redux';
import ScheduleTooltip from '../../../ScheduleTooltip';
import {
  StyledButtonBlock,
  StyledSubtitle,
  StyledTitle,
  StyledHeader,
  StyledInfoWindow,
  UploadAndDownoadButtons,
  PopoverWrapper,
  PopoverContent,
  FlexCol,
  Title,
  Description,
  DownloadButton,
  Wrapper,
  StyledIcon,
  CircularProgressOverlay,
  WaterProgressContainer,
  WaterFill,
  WaterProgressText,
  PopoverHeader,
  StyledCloseIcon,
} from './styles';

const UploadDownloadSection = memo(({
  onDownloadTemplateClicked,
  setUploadButtonClicked,
  uploadProgress = 10,
  uploadStatus = '',
}) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const isLoading = useSelector((state) => requestInProcess(state, 'SCHEDULES_IMPORT'));

  const handleUploadClick = useCallback(() => {
    setUploadButtonClicked(true);
  }, []);

  const togglePopover = useCallback(() => {
    setPopoverVisible((prev) => !prev);
  }, []);

  const handleDownloadClick = useCallback(() => {
    onDownloadTemplateClicked();
    setPopoverVisible(false);
  }, [onDownloadTemplateClicked]);

  const getDisplayProgress = () => {
    if (uploadStatus === 'loading') {
      return uploadProgress;
    }
    return 10;
  };

  return (
    <StyledInfoWindow>
      <StyledHeader>
        <StyledTitle>Prefer to Upload Property Info?</StyledTitle>
        <StyledSubtitle>Upload your own file or download our template to fill in property details offline, then upload it here</StyledSubtitle>
      </StyledHeader>
      <StyledButtonBlock>
        <UploadAndDownoadButtons disabled={isLoading} onClick={handleUploadClick}>
          <Wrapper><StyledIcon icon='upload' />Upload Data <ScheduleTooltip width={1.2} text="Upload a file if you already have your property data" /></Wrapper>
        </UploadAndDownoadButtons>
        <UploadAndDownoadButtons onClick={togglePopover}>
          <Wrapper><StyledIcon icon='downloadIcon' />Download our Template <ScheduleTooltip width={1.2} text="Download our template, fill it out, and upload it later" /></Wrapper>
        </UploadAndDownoadButtons>
        {popoverVisible && (
          <PopoverWrapper>
            <PopoverContent tabIndex={-1} role="dialog">
              <FlexCol>
                <PopoverHeader>
                  <Title>Excel Template</Title>
                  <StyledCloseIcon icon='close' onClick={() => setPopoverVisible(false)} />
                </PopoverHeader>
                <Description>
                  Download our pre-formatted Excel template to ensure your
                  data is properly structured.
                </Description>
                <DownloadButton onClick={handleDownloadClick}>
                  Download Template
                </DownloadButton>
              </FlexCol>
            </PopoverContent>
          </PopoverWrapper>
        )}
      </StyledButtonBlock>

      {isLoading && (
        <CircularProgressOverlay>
          <WaterProgressContainer>
            <WaterFill progress={getDisplayProgress()} />
            <WaterProgressText>
              <div>Uploading... {getDisplayProgress()}%</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Please wait</div>
            </WaterProgressText>
          </WaterProgressContainer>
        </CircularProgressOverlay>
      )}
    </StyledInfoWindow>
  );
});

UploadDownloadSection.displayName = 'UploadDownloadSection';

UploadDownloadSection.propTypes = {
  onDownloadTemplateClicked: PropTypes.func.isRequired,
  setUploadButtonClicked: PropTypes.func.isRequired,
  uploadProgress: PropTypes.number,
  uploadStatus: PropTypes.oneOf(['loading', 'completed']),
};

export default UploadDownloadSection;
